/**
 * The salon's widget look (boona docs/codebase/widget-appearance/README.md): its button
 * colour and header image, set in the dashboard's Settings → Online booking or brought
 * in from YClients. Buttons paint with the --primary / --primary-foreground variables
 * (styles/widget.css), so the look is two variables on the shadow host — inline styles
 * there win over the stylesheet's :host defaults.
 */
import { getCompanyRegistry } from "@/lib/company/companyRegistrySingleton";
import type { WidgetLook, WidgetPromo } from "@/lib/types/company";
import { getApiBaseUrl } from "../publicSessionService";

const HEX = /^#?([0-9a-fA-F]{6})$/;

/** "#FF00B1" → "321.6 100% 50%", the `H S% L%` triplet the CSS variables hold. */
export function hexToHslTriplet(hex: string): string | null {
  const m = HEX.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  const round = (v: number) => Math.round(v * 10) / 10;
  return `${round(h)} ${round(s * 100)}% ${round(l * 100)}%`;
}

const VARS = ["--primary", "--primary-foreground"] as const;

/** Paint the host with the salon's colour — or give the stylesheet's back (null look). */
export function applyWidgetLook(host: HTMLElement | null | undefined, look: WidgetLook | null | undefined): void {
  if (!host) return;
  const primary = look?.main_color ? hexToHslTriplet(look.main_color) : null;
  if (!primary) {
    VARS.forEach((v) => host.style.removeProperty(v));
    return;
  }
  host.style.setProperty("--primary", primary);
  const text = look?.main_text_color ? hexToHslTriplet(look.main_text_color) : null;
  if (text) host.style.setProperty("--primary-foreground", text);
  else host.style.removeProperty("--primary-foreground");
}

export function getWidgetLook(): WidgetLook | null {
  return getCompanyRegistry().getSettings()?.widget_look ?? null;
}

/** A /media/… path from the settings as a URL on the salon's API host. */
export function mediaUrl(path: string): string {
  return /^https?:\/\//.test(path) ? path : `${getApiBaseUrl()}${path}`;
}

export function getPromos(): WidgetPromo[] {
  return getWidgetLook()?.promos ?? [];
}
